// src/pages/aspirant/Dashboard.tsx - UPDATED VERSION
import React, { useEffect, useState } from 'react';
import { 
  BarChart3, Users, MessageSquare, FileText, AlertTriangle,
  MapPin, DollarSign, RefreshCw, AlertCircle, Shield,
  Calendar, Activity, Bell, Eye, Phone, Mail, CheckCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';

// Dashboard stats interface
interface DashboardStats {
  totalMemorandums: number;
  activeCampaigns: number;
  reportsInWard: number;
  citizensReached: number;
  smsCredits: number;
  subscriptionDaysLeft: number;
  totalReports?: number;
  pendingReports?: number;
  resolvedReports?: number;
  urgentReports?: number;
  wardCitizenCount?: number;
}

// Simple interfaces for data display
interface SimpleCampaign {
  id: number;
  campaignName: string;
  description?: string;
  status: string;
  createdAt: string;
  smsSent: boolean;
  smsRecipients?: number;
}

interface SimpleMemorandum {
  id: number;
  memorandumNumber?: string;
  subject: string;
  recipientDepartment: string;
  type: string;
  generatedAt: string;
  status?: string;
}

// Extended Aspirant type
interface ExtendedAspirant {
  id: number;
  name: string;
  phoneNumber: string;
  email?: string;
  position: string;
  tier?: string;
  constituency?: string;
  ward?: string;
  party?: string;
  languagePreference?: string;
  receiveSmsAlerts?: boolean;
  receiveEmailAlerts?: boolean;
  apiKey?: string;
  createdAt?: string;
  lastLogin?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  constituencyName?: string;
  wardName?: string;
  constituencyId?: number;
  wardId?: number;
}

const AspirantDashboard: React.FC = () => {
  const { aspirant: authAspirant, token } = useAuth();
  const [aspirant, setAspirant] = useState<ExtendedAspirant | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalMemorandums: 0,
    activeCampaigns: 0,
    reportsInWard: 0,
    citizensReached: 0,
    smsCredits: 1000,
    subscriptionDaysLeft: 30,
  });
  const [recentMemorandums, setRecentMemorandums] = useState<SimpleMemorandum[]>([]);
  const [recentCampaigns, setRecentCampaigns] = useState<SimpleCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all dashboard data using API service
  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      setError(null);

      console.log('🔍 Auth Context Debug:', {
        authAspirant,
        hasId: !!authAspirant?.id,
        token: token ? 'Present' : 'Missing'
      });

      if (!authAspirant?.id) {
        throw new Error('No aspirant ID found in auth context. Please login again.');
      }

      const aspirantId = authAspirant.id;
      console.log('📊 Fetching dashboard for aspirant ID:', aspirantId);

      // TRY 1: Use consolidated dashboard endpoint via API service
      try {
        console.log('🔄 Trying consolidated dashboard endpoint...');
        const dashboardResponse = await apiService.getAspirantDashboard(aspirantId);
        
        console.log('📦 Dashboard API Response:', dashboardResponse.data);
        
        if (dashboardResponse.data?.success && dashboardResponse.data.data) {
          console.log('✅ Dashboard endpoint success');
          const dashboardData = dashboardResponse.data.data;
          
          // Set aspirant info
          const updatedAspirant: ExtendedAspirant = {
            id: aspirantId,
            name: authAspirant.name,
            phoneNumber: authAspirant.phoneNumber,
            email: authAspirant.email,
            position: authAspirant.position,
            tier: authAspirant.tier,
            party: authAspirant.party,
            languagePreference: authAspirant.languagePreference,
            receiveSmsAlerts: authAspirant.receiveSmsAlerts,
            receiveEmailAlerts: authAspirant.receiveEmailAlerts,
            wardId: dashboardData.aspirant?.wardId,
            wardName: dashboardData.aspirant?.wardName,
            constituencyName: dashboardData.aspirant?.constituencyName,
            constituency: dashboardData.aspirant?.constituencyName,
            ward: dashboardData.aspirant?.wardName,
            createdAt: dashboardData.aspirant?.createdAt,
            subscriptionStartDate: dashboardData.aspirant?.subscriptionStartDate,
            subscriptionEndDate: dashboardData.aspirant?.subscriptionEndDate,
            lastLogin: dashboardData.aspirant?.lastLogin,
          };
          
          setAspirant(updatedAspirant);

          // Set dashboard stats
          const dashboardStats: DashboardStats = {
            totalMemorandums: dashboardData.totalMemorandums || 0,
            activeCampaigns: dashboardData.activeCampaigns || 0,
            reportsInWard: dashboardData.reportsInWard || 0,
            citizensReached: dashboardData.citizensReached || dashboardData.wardCitizenCount || 0,
            smsCredits: dashboardData.smsCredits || 1000,
            subscriptionDaysLeft: dashboardData.subscriptionDaysLeft || 30,
            wardCitizenCount: dashboardData.wardCitizenCount || 0,
            totalReports: dashboardData.totalReports || 0,
            pendingReports: dashboardData.pendingReports || 0,
            resolvedReports: dashboardData.resolvedReports || 0,
            urgentReports: dashboardData.urgentReports || 0,
          };
          
          setStats(dashboardStats);

          // Set recent data
          setRecentMemorandums(dashboardData.recentMemorandums || []);
          setRecentCampaigns(dashboardData.recentCampaigns || []);

          setLoading(false);
          setRefreshing(false);
          toast.success('Dashboard data loaded successfully');
          return;
        } else {
          console.log('⚠️ Dashboard API returned unsuccessful response');
          throw new Error(dashboardResponse.data?.error || 'Dashboard API returned unsuccessful response');
        }
      } catch (dashboardError: any) {
        console.log('⚠️ Consolidated dashboard endpoint failed:', dashboardError.message);
        console.log('Falling back to individual endpoints...');
      }

      // TRY 2: Fallback to individual API calls using API service
      await fetchIndividualData();

    } catch (err: any) {
      console.error('❌ Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard');
      
      // Final fallback: Use demo data
      setFallbackData();
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fallback: Fetch data from individual endpoints using API service
  const fetchIndividualData = async () => {
    if (!authAspirant?.id) {
      throw new Error('No aspirant ID available for individual API calls');
    }

    const aspirantId = authAspirant.id;
    console.log('🔄 Fetching individual data for aspirant:', aspirantId);

    try {
      // Execute API calls in parallel for better performance
      const [aspirantData, memorandumsData, campaignsData, citizenCount, wardReports] = await Promise.all([
        // 1. Get aspirant with ward info
        apiService.getAspirantWithWard(aspirantId.toString())
          .then(response => {
            console.log('✅ Aspirant data loaded:', response.data?.data);
            return response.data?.data;
          })
          .catch(error => {
            console.log('❌ Failed to load aspirant data:', error.message);
            return null;
          }),

        // 2. Get memorandums
        apiService.getAspirantMemorandums(aspirantId)
          .then(response => {
            const memos = response.data?.data || response.data || [];
            console.log('✅ Memorandums data loaded, count:', Array.isArray(memos) ? memos.length : 0);
            return memos;
          })
          .catch(error => {
            console.log('❌ Failed to load memorandums:', error.message);
            return [];
          }),

        // 3. Get campaigns - IMPORTANT: This is where we need to fix the counting
        apiService.getAspirantCampaigns(aspirantId.toString())
          .then(response => {
            const campaigns = response.data?.data || response.data || [];
            console.log('✅ Campaigns data loaded, total count:', Array.isArray(campaigns) ? campaigns.length : 0);
            console.log('📋 Campaign statuses:', Array.isArray(campaigns) ? campaigns.map((c: any) => c.status) : []);
            return campaigns;
          })
          .catch(error => {
            console.log('❌ Failed to load campaigns:', error.message);
            return [];
          }),

        // 4. Get citizen count in ward (if ward ID available)
        Promise.resolve().then(async () => {
          try {
            const aspirantResp = await apiService.getAspirantWithWard(aspirantId.toString());
            const wardId = aspirantResp.data?.data?.wardId;
            if (wardId) {
              const count = await apiService.getWardCitizenCount(wardId);
              console.log('✅ Citizen count loaded:', count);
              return { wardId, count };
            }
            return { wardId: null, count: 0 };
          } catch (error) {
            console.log('❌ Failed to load citizen count:', error);
            return { wardId: null, count: 0 };
          }
        }),

        // 5. Get reports in ward (if ward ID available)
        Promise.resolve().then(async () => {
          try {
            const aspirantResp = await apiService.getAspirantWithWard(aspirantId.toString());
            const wardId = aspirantResp.data?.data?.wardId;
            if (wardId) {
              const reportsResp = await apiService.getReportsByWard(wardId);
              const reports = reportsResp.data?.data || reportsResp.data || [];
              console.log('✅ Ward reports loaded, count:', Array.isArray(reports) ? reports.length : 0);
              return { wardId, reports };
            }
            return { wardId: null, reports: [] };
          } catch (error) {
            console.log('❌ Failed to load ward reports:', error);
            return { wardId: null, reports: [] };
          }
        })
      ]);

      // Extract data from responses
      const [aspirantInfo, memorandums, campaigns, wardInfo, reportsInfo] = [
        aspirantData,
        memorandumsData,
        campaignsData,
        citizenCount,
        wardReports
      ];

      // Set aspirant info
      if (aspirantInfo) {
        const updatedAspirant: ExtendedAspirant = {
          id: aspirantId,
          name: authAspirant.name,
          phoneNumber: authAspirant.phoneNumber,
          email: authAspirant.email,
          position: authAspirant.position,
          tier: authAspirant.tier,
          party: authAspirant.party,
          wardId: aspirantInfo.wardId,
          wardName: aspirantInfo.wardName || aspirantInfo.ward,
          constituencyName: aspirantInfo.constituencyName || aspirantInfo.constituency,
          constituency: aspirantInfo.constituencyName || aspirantInfo.constituency,
          ward: aspirantInfo.wardName || aspirantInfo.ward,
          languagePreference: authAspirant.languagePreference,
          receiveSmsAlerts: authAspirant.receiveSmsAlerts,
          receiveEmailAlerts: authAspirant.receiveEmailAlerts,
        };
        
        setAspirant(updatedAspirant);
        console.log('✅ Aspirant info set:', updatedAspirant);
      }

      const wardId = wardInfo.wardId || aspirantInfo?.wardId;
      const citizenCountValue = wardInfo.count || 0;
      const wardReportsData = reportsInfo.reports || [];

      // Calculate reports stats
      const reportsInWard = Array.isArray(wardReportsData) ? wardReportsData.length : 0;
      const pendingReports = Array.isArray(wardReportsData) ? 
        wardReportsData.filter((r: any) => r.status === 'PENDING' || r.status === 'OPEN').length : 0;
      const resolvedReports = Array.isArray(wardReportsData) ? 
        wardReportsData.filter((r: any) => r.status === 'RESOLVED' || r.status === 'CLOSED').length : 0;
      const urgentReports = Array.isArray(wardReportsData) ? 
        wardReportsData.filter((r: any) => (r.urgencyScore || 0) >= 7).length : 0;

      // ============================================================
      // FIX: Calculate campaigns stats correctly
      // Active campaigns should include: PUBLISHED, ACTIVE, SCHEDULED, SMS_SENT
      // ============================================================
      const campaignsArray = Array.isArray(campaigns) ? campaigns : [];
      const totalCampaigns = campaignsArray.length;
      
      // Define which statuses are considered "active"
      const activeStatuses = ['PUBLISHED', 'ACTIVE', 'SCHEDULED', 'SMS_SENT', 'SMS_PARTIALLY_SENT', 'IN_PROGRESS'];
      
      const activeCampaigns = campaignsArray.filter((c: any) => {
        const status = c.status?.toUpperCase() || '';
        return activeStatuses.includes(status);
      }).length;

      console.log(`📊 Campaign stats: Total=${totalCampaigns}, Active=${activeCampaigns}`);
      console.log(`📋 Campaign statuses:`, campaignsArray.map((c: any) => c.status));
      // ============================================================

      // Calculate memorandums stats
      const memorandumsArray = Array.isArray(memorandums) ? memorandums : [];
      const totalMemorandums = memorandumsArray.length;

      // Set recent data (first 5 items)
      setRecentMemorandums(memorandumsArray.slice(0, 5).map((memo: any) => ({
        id: memo.id || memo.memorandumId || 0,
        memorandumNumber: memo.memorandumNumber,
        subject: memo.subject,
        recipientDepartment: memo.recipientDepartment,
        type: memo.type,
        status: memo.status,
        generatedAt: memo.generatedAt || memo.createdAt || new Date().toISOString(),
      })));

      setRecentCampaigns(campaignsArray.slice(0, 5).map((campaign: any) => ({
        id: campaign.id || campaign.campaignId || 0,
        campaignName: campaign.campaignName,
        description: campaign.description,
        status: campaign.status,
        createdAt: campaign.createdAt || new Date().toISOString(),
        smsSent: campaign.smsSent || false,
        smsRecipients: campaign.smsRecipients || campaign.recipientCount || 0,
      })));

      // Set stats
      const newStats: DashboardStats = {
        totalMemorandums,
        activeCampaigns,
        reportsInWard,
        citizensReached: citizenCountValue,
        smsCredits: 1000,
        subscriptionDaysLeft: 30,
        wardCitizenCount: citizenCountValue,
        totalReports: reportsInWard,
        pendingReports,
        resolvedReports,
        urgentReports,
      };
      
      setStats(newStats);
      console.log('✅ Stats set:', newStats);

      toast.success('Dashboard loaded using individual endpoints');

    } catch (error: any) {
      console.error('❌ Error in individual data fetch:', error);
      throw error;
    }
  };

  // Final fallback: Use demo/hardcoded data
  const setFallbackData = () => {
    console.log('🆘 Using fallback demonstration data');
    
    const fallbackAspirant: ExtendedAspirant = {
      id: authAspirant?.id || 9,
      name: authAspirant?.name || 'Aspirant User',
      phoneNumber: authAspirant?.phoneNumber || '+254700000000',
      email: authAspirant?.email || 'aspirant@example.com',
      position: authAspirant?.position || 'MCA',
      tier: authAspirant?.tier || 'FREE',
      constituency: authAspirant?.constituency || 'Sample Constituency',
      ward: 'Sample Ward',
      party: authAspirant?.party || 'Party',
      languagePreference: authAspirant?.languagePreference || 'en',
      wardId: 1,
      wardName: 'Sample Ward',
      constituencyName: 'Sample Constituency',
      receiveSmsAlerts: true,
      receiveEmailAlerts: true,
    };
    
    setAspirant(fallbackAspirant);
    
    setStats({
      totalMemorandums: 5,
      activeCampaigns: 3,
      reportsInWard: 18,
      citizensReached: 450,
      smsCredits: 1000,
      subscriptionDaysLeft: 30,
      wardCitizenCount: 450,
      totalReports: 18,
      pendingReports: 8,
      resolvedReports: 6,
      urgentReports: 4,
    });
    
    setRecentMemorandums([
      {
        id: 1,
        memorandumNumber: 'MEM-2024-001',
        subject: 'Road Repairs Request',
        recipientDepartment: 'County Public Works',
        type: 'COUNTY',
        status: 'DRAFT',
        generatedAt: new Date().toISOString(),
      },
      {
        id: 2,
        memorandumNumber: 'MEM-2024-002',
        subject: 'Water Supply Issues',
        recipientDepartment: 'Water Services Department',
        type: 'COUNTY',
        status: 'SENT',
        generatedAt: new Date(Date.now() - 86400000).toISOString(),
      }
    ]);
    
    setRecentCampaigns([
      {
        id: 1,
        campaignName: 'Community Outreach',
        description: 'Engaging with local residents',
        status: 'ACTIVE',
        createdAt: new Date().toISOString(),
        smsSent: true,
        smsRecipients: 250
      },
      {
        id: 2,
        campaignName: 'Youth Employment Initiative',
        description: 'Creating job opportunities for youth',
        status: 'SCHEDULED',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        smsSent: false,
        smsRecipients: 0
      },
      {
        id: 3,
        campaignName: 'Health Awareness Campaign',
        description: 'Promoting health awareness in the community',
        status: 'PUBLISHED',
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        smsSent: true,
        smsRecipients: 150
      }
    ]);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [authAspirant?.id]);

  const handleRefresh = () => {
    fetchDashboardData();
    toast.success('Refreshing dashboard data...');
  };

  const StatCard: React.FC<{
    title: string;
    value: number | string;
    icon: React.ReactNode;
    color: string;
    tooltip?: string;
    suffix?: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, tooltip, suffix, onClick }) => (
    <div 
      className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer hover:border-[#3c8d2e]' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 flex items-center gap-1">
            {title}
            {tooltip && (
              <span className="text-xs text-gray-400 cursor-help" title={tooltip}>
                (i)
              </span>
            )}
          </p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {typeof value === 'number' ? value.toLocaleString() : value}
            {suffix && <span className="text-sm font-normal ml-1">{suffix}</span>}
          </p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          {icon}
        </div>
      </div>
    </div>
  );

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
      DRAFT: { color: 'text-[#854d0e]', bg: 'bg-[#fefce8]', label: 'Draft' },
      PUBLISHED: { color: 'text-[#0b2045]', bg: 'bg-[#e8f1ec]', label: 'Published' },
      PENDING: { color: 'text-[#0b2045]', bg: 'bg-[#e8f1ec]', label: 'Pending' },
      REJECTED: { color: 'text-red-800', bg: 'bg-red-100', label: 'Rejected' },
      SCHEDULED: { color: 'text-[#0b2045]', bg: 'bg-[#e8f1ec]', label: 'Scheduled' },
      ACTIVE: { color: 'text-[#0b2045]', bg: 'bg-[#3c8d2e]/10', label: 'Active' },
      COMPLETED: { color: 'text-[#3c8d2e]', bg: 'bg-[#3c8d2e]/10', label: 'Completed' },
      CANCELLED: { color: 'text-gray-800', bg: 'bg-gray-100', label: 'Cancelled' },
      SMS_SENT: { color: 'text-[#0b2045]', bg: 'bg-[#0b2045]/10', label: 'SMS Sent' },
      SMS_PARTIALLY_SENT: { color: 'text-[#ca8a04]', bg: 'bg-[#fefce8]', label: 'Partial SMS' },
      SMS_FAILED: { color: 'text-red-800', bg: 'bg-red-100', label: 'SMS Failed' },
      VERIFIED: { color: 'text-[#3c8d2e]', bg: 'bg-[#3c8d2e]/10', label: 'Verified' },
      IN_PROGRESS: { color: 'text-[#ca8a04]', bg: 'bg-[#fefce8]', label: 'In Progress' },
      RESOLVED: { color: 'text-[#3c8d2e]', bg: 'bg-[#3c8d2e]/10', label: 'Resolved' },
      ESCALATED: { color: 'text-red-800', bg: 'bg-red-100', label: 'Escalated' },
      COUNTY: { color: 'text-[#0b2045]', bg: 'bg-[#0b2045]/10', label: 'County' },
      NATIONAL: { color: 'text-[#0b2045]', bg: 'bg-[#0b2045]/10', label: 'National' },
      ASSEMBLY: { color: 'text-[#0b2045]', bg: 'bg-[#0b2045]/10', label: 'Assembly' },
      SENT: { color: 'text-[#0b2045]', bg: 'bg-[#0b2045]/10', label: 'Sent' },
      OPEN: { color: 'text-[#ca8a04]', bg: 'bg-[#fefce8]', label: 'Open' },
      CLOSED: { color: 'text-gray-800', bg: 'bg-gray-100', label: 'Closed' },
    };

    const config = statusConfig[status] || { color: 'text-gray-800', bg: 'bg-gray-100', label: status };
    
    return (
      <span className={`px-2 py-1 ${config.bg} ${config.color} text-xs font-medium rounded`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0b2045] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
          <p className="text-sm text-gray-500 mt-2">Fetching data from backend...</p>
        </div>
      </div>
    );
  }

  // Safely access aspirant properties
  const displayAspirant = aspirant || {
    id: 0,
    name: '',
    phoneNumber: '',
    position: '',
  };

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0b2045]">Aspirant Dashboard</h1>
          <p className="text-gray-600">Welcome back, {displayAspirant.name || 'Aspirant'}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <span className="px-3 py-1 bg-[#e8f1ec] text-[#0b2045] text-sm font-medium rounded-full">
              {displayAspirant.position || 'Aspirant'}
            </span>
            {displayAspirant.party && (
              <span className="px-3 py-1 bg-[#3c8d2e]/10 text-[#0b2045] text-sm font-medium rounded-full">
                {displayAspirant.party}
              </span>
            )}
            {displayAspirant.tier && (
              <span className="px-3 py-1 bg-[#0b2045]/10 text-[#0b2045] text-sm font-medium rounded-full">
                {displayAspirant.tier} Tier
              </span>
            )}
            {(displayAspirant.constituencyName || displayAspirant.constituency) && (
              <span className="px-3 py-1 bg-[#3c8d2e]/10 text-[#0b2045] text-sm font-medium rounded-full">
                {displayAspirant.constituencyName || displayAspirant.constituency}
              </span>
            )}
            {(displayAspirant.wardName || displayAspirant.ward) && (
              <span className="px-3 py-1 bg-[#0b2045]/5 text-[#0b2045] text-sm font-medium rounded-full">
                Ward: {displayAspirant.wardName || displayAspirant.ward}
              </span>
            )}
            {displayAspirant.wardId && (
              <span className="px-3 py-1 bg-[#fefce8] text-[#ca8a04] text-sm font-medium rounded-full">
                Ward ID: {displayAspirant.wardId}
              </span>
            )}
            {error && (
              <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                API Error - Using Demo Data
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-[#f4f7f6] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-red-800">API Connection Issue</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-sm text-red-600 mt-1">Using demonstration data. Check backend connection.</p>
              <button 
                onClick={() => {
                  console.log('Debug info:', { aspirant: displayAspirant, stats, recentMemorandums, recentCampaigns });
                  toast.success('Debug info logged to console');
                }}
                className="mt-2 text-xs text-[#3c8d2e] hover:text-[#2d7022] font-medium"
              >
                View Debug Info
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aspirant Contact Info */}
      <div className="bg-gradient-to-r from-[#f4f7f6] to-[#e8f1ec] border border-[#0b2045]/10 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-[#0b2045] mb-2">Your Profile</h3>
            <div className="flex flex-wrap gap-3">
              {displayAspirant.phoneNumber && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{displayAspirant.phoneNumber}</span>
                </div>
              )}
              {displayAspirant.email && (
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{displayAspirant.email}</span>
                </div>
              )}
              {displayAspirant.createdAt && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    Joined {formatDate(displayAspirant.createdAt)}
                  </span>
                </div>
              )}
              {displayAspirant.lastLogin && (
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">
                    Last login: {formatDate(displayAspirant.lastLogin)}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button 
            onClick={() => toast.success('Opening profile editor...')}
            className="px-4 py-2 bg-white border border-[#3c8d2e] text-[#3c8d2e] rounded-lg hover:bg-[#e8f1ec]"
          >
            Edit Profile
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Memorandums"
          value={stats.totalMemorandums}
          icon={<FileText className="text-[#0b2045]" size={24} />}
          color="bg-[#0b2045]/10"
          tooltip="Total memorandums submitted by you"
          onClick={() => toast.success('Opening your memorandums...')}
        />
        <StatCard
          title="Active Campaigns"
          value={stats.activeCampaigns}
          icon={<Activity className="text-[#3c8d2e]" size={24} />}
          color="bg-[#3c8d2e]/10"
          tooltip="Your currently running campaigns (Published, Active, Scheduled, SMS Sent)"
          onClick={() => toast.success('Opening your campaigns...')}
        />
        <StatCard
          title="Ward Reports"
          value={stats.reportsInWard}
          icon={<MapPin className="text-[#ca8a04]" size={24} />}
          color="bg-[#fefce8]"
          tooltip={`Reports from your ward: ${displayAspirant.wardName || displayAspirant.ward || 'Your Ward'}`}
          onClick={() => toast.success('Opening reports from your ward...')}
        />
        <StatCard
          title="Citizens in Ward"
          value={stats.citizensReached}
          icon={<Users className="text-[#3c8d2e]" size={24} />}
          color="bg-[#3c8d2e]/10"
          tooltip={`Total citizens registered in ${displayAspirant.wardName || displayAspirant.ward || 'your ward'}`}
          onClick={() => toast.success('Viewing citizen analytics for your ward...')}
        />
        <StatCard
          title="SMS Credits"
          value={stats.smsCredits}
          icon={<DollarSign className="text-[#0b2045]" size={24} />}
          color="bg-[#0b2045]/10"
          tooltip={`Remaining SMS credits for ${displayAspirant.tier || 'FREE'} tier`}
          onClick={() => toast.success('Viewing SMS credit details...')}
        />
        <StatCard
          title="Subscription Days"
          value={stats.subscriptionDaysLeft}
          icon={<Calendar className="text-red-500" size={24} />}
          color="bg-red-50"
          tooltip="Days left in your subscription"
          suffix="days"
          onClick={() => toast.success('Viewing your subscription details...')}
        />
        {stats.pendingReports !== undefined && (
          <StatCard            title="Pending Reports"
            value={stats.pendingReports}
            icon={<AlertCircle className="text-[#ca8a04]" size={24} />}
            color="bg-[#fefce8]"
            tooltip="Pending reports in your ward"
            onClick={() => toast.success('Viewing pending reports...')}
          />
        )}
        {stats.resolvedReports !== undefined && (
          <StatCard
            title="Resolved Reports"
            value={stats.resolvedReports}
            icon={<CheckCircle className="text-[#3c8d2e]" size={24} />}
            color="bg-[#3c8d2e]/10"
            tooltip="Resolved reports in your ward"
            onClick={() => toast.success('Viewing resolved reports...')}
          />
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#0b2045]">Quick Actions</h3>
          <span className="text-sm text-gray-500">Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button 
            onClick={() => toast.success('Redirecting to memorandum creation...')}
            className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] hover:border-[#0b2045] transition-colors"
          >
            <FileText className="text-[#0b2045] mb-2" size={24} />
            <span className="text-sm font-medium">New Memorandum</span>
          </button>
          <button 
            onClick={() => toast.success('Redirecting to campaign creation...')}
            className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] hover:border-[#3c8d2e] transition-colors"
          >
            <MessageSquare className="text-[#3c8d2e] mb-2" size={24} />
            <span className="text-sm font-medium">New Campaign</span>
          </button>
          <button 
            onClick={() => toast.success('Opening reports dashboard...')}
            className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] hover:border-[#ca8a04] transition-colors"
          >
            <Eye className="text-[#ca8a04] mb-2" size={24} />
            <span className="text-sm font-medium">View Reports</span>
          </button>
          <button 
            onClick={() => toast.success('Loading analytics dashboard...')}
            className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] hover:border-[#0b2045] transition-colors"
          >
            <BarChart3 className="text-[#0b2045] mb-2" size={24} />
            <span className="text-sm font-medium">Analytics</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Memorandums */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#0b2045]">Your Recent Memorandums</h3>
            <button 
              onClick={() => toast.success('Viewing all your memorandums...')}
              className="text-sm text-[#3c8d2e] hover:text-[#2d7022] font-medium"
            >
              View All →
            </button>
          </div>
          {recentMemorandums.length > 0 ? (
            <div className="space-y-3">
              {recentMemorandums.map((memo) => (
                <div key={memo.id} className="p-3 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0b2045] truncate">
                        {memo.memorandumNumber ? `${memo.memorandumNumber}: ` : ''}{memo.subject}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-600">
                          To: {memo.recipientDepartment}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {formatDate(memo.generatedAt)}
                        </span>
                      </div>
                    </div>
                    {getStatusBadge(memo.status || memo.type)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No memorandums yet</p>
              <button 
                onClick={() => toast.success('Creating first memorandum...')}
                className="mt-2 text-sm text-[#3c8d2e] hover:text-[#2d7022] font-medium"
              >
                Create your first memorandum
              </button>
            </div>
          )}
        </div>

        {/* Recent Campaigns */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#0b2045]">Your Recent Campaigns</h3>
            <button 
              onClick={() => toast.success('Viewing all your campaigns...')}
              className="text-sm text-[#3c8d2e] hover:text-[#2d7022] font-medium"
            >
              View All →
            </button>
          </div>
          {recentCampaigns.length > 0 ? (
            <div className="space-y-3">
              {recentCampaigns.map((campaign) => (
                <div key={campaign.id} className="p-3 border border-gray-200 rounded-lg hover:bg-[#f4f7f6] transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0b2045] truncate">{campaign.campaignName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-gray-600 truncate">
                          {campaign.description || 'No description'}
                        </span>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs text-gray-500">
                          {formatDate(campaign.createdAt)}
                        </span>
                      </div>
                      {campaign.smsSent && (
                        <div className="flex items-center gap-1 mt-1">
                          <MessageSquare className="h-3 w-3 text-[#3c8d2e]" />
                          <span className="text-xs text-[#3c8d2e]">
                            SMS sent to {campaign.smsRecipients || 0} recipients
                          </span>
                        </div>
                      )}
                    </div>
                    {getStatusBadge(campaign.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No campaigns yet</p>
              <button 
                onClick={() => toast.success('Creating first campaign...')}
                className="mt-2 text-sm text-[#3c8d2e] hover:text-[#2d7022] font-medium"
              >
                Launch your first campaign
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ward Information Section */}
      {(displayAspirant.wardId || displayAspirant.wardName || displayAspirant.ward) && (
        <div className="bg-gradient-to-r from-[#0b2045]/5 to-[#3c8d2e]/5 border border-[#3c8d2e]/20 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#0b2045] mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#3c8d2e]" />
            Ward Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">Ward Name</p>
              <p className="text-lg font-bold text-[#0b2045] mt-1">
                {displayAspirant.wardName || displayAspirant.ward || 'Not specified'}
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">Ward ID</p>
              <p className="text-lg font-bold text-[#0b2045] mt-1">
                {displayAspirant.wardId || 'Not specified'}
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <p className="text-sm text-gray-600">Constituency</p>
              <p className="text-lg font-bold text-[#0b2045] mt-1">
                {displayAspirant.constituencyName || displayAspirant.constituency || 'Not specified'}
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-3 bg-white/50 rounded-lg">
              <p className="text-sm text-gray-600">Citizens Registered</p>
              <p className="text-xl font-bold text-[#0b2045]">
                {stats.citizensReached?.toLocaleString() || '0'} citizens
              </p>
            </div>
            <div className="p-3 bg-white/50 rounded-lg">
              <p className="text-sm text-gray-600">Reports in Ward</p>
              <p className="text-xl font-bold text-[#0b2045]">
                {stats.reportsInWard?.toLocaleString() || '0'} reports
              </p>
            </div>
          </div>
          <div className="mt-4 p-3 bg-[#e8f1ec] border border-[#3c8d2e]/30 rounded-lg">
            <p className="text-sm text-[#0b2045]">
              <strong>Note:</strong> This data is specific to your assigned ward. You can only view and interact with data from {displayAspirant.wardName || 'your ward'}.
            </p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-sm text-gray-500 pt-4 border-t border-gray-200">
        <p>Aspirant Dashboard</p>
        <p className="mt-1">
          Data loaded for Aspirant: {displayAspirant.name} (ID: {displayAspirant.id})
          {displayAspirant.wardId && ` | Ward: ${displayAspirant.wardName || displayAspirant.ward} (ID: ${displayAspirant.wardId})`}
        </p>
        <p className="mt-1">Data is specific to your account and assigned ward</p>
        {error && (
          <p className="mt-1 text-red-500">⚠️ Backend API unavailable - Using demonstration data</p>
        )}
        <p className="mt-1 text-xs text-gray-400">
          Last refreshed: {new Date().toLocaleTimeString()} | Version: 1.0.0
        </p>
      </div>
    </div>
  );
};

export default AspirantDashboard;